import type { SetRequired } from '@eturi/ts-helpers'
import { assertNotNullish } from '@eturi/util'
import { toParamsURL } from '@op/util'
import { bindCreateAsyncThunkToState } from '../bindCreateAsyncThunkToState.js'
import { featureSet$, flags$ } from '../compound-selectors/feature-flags.js'
import { onboarding$ } from '../compound-selectors/onboarding.js'
import type { HttpExtra } from '../http.js'
import { isAuthenticated$ } from '../reducers/account.slice.js'
import type {
	WithAccountState,
	WithFeatureFlagsState,
	WithOnboardingState,
} from '../reducers/index.js'
import type { SystemInfo, SystemInfoType } from '../types/SystemInfo.js'

type SystemInfoThunkState = WithAccountState & WithFeatureFlagsState & WithOnboardingState

const createAsyncThunk = /*@__PURE__*/ bindCreateAsyncThunkToState<SystemInfoThunkState>()

type SystemInfoFetchArgs = {
	readonly flags: boolean
	readonly onboarding: boolean
}

type SystemInfoArg = HttpExtra & Partial<SystemInfoFetchArgs>

export const fetchSystemInfo = /*@__PURE__*/ createAsyncThunk(
	'systemInfo/fetch',
	async (arg: SystemInfoArg = {}, { dispatch, getState, extra: { http } }) => {
		const { flags, onboarding, ...extra } = parseSystemInfoFetchArgs(arg)
		const state = getState()
		const systemInfoTypes: SystemInfoType[] = []

		if (flags) systemInfoTypes.push('feature_flags')
		if (onboarding) systemInfoTypes.push('onboarding_choices')

		const url = toParamsURL('/system', {
			extras: systemInfoTypes.join(','),
			// If requesting feature flags, include feature_set if available. This would happen on
			// `force` calls when we're trying to verify that the feature set is still valid. Passing
			// this informs the server to give us the same feature set if it's still valid.
			feature_set: (flags && featureSet$(state)) || undefined,
		})

		const isUnauthenticated = !isAuthenticated$(state)

		const systemInfo = await dispatch(
			http.get<Maybe<Partial<SystemInfo>>>(url, { ...extra, isUnauthenticated }),
		)

		assertNotNullish(systemInfo, 'SystemInfo')

		return systemInfo
	},
	{
		condition: (arg = {}, api) => {
			const { flags, onboarding, force } = parseSystemInfoFetchArgs(arg)

			// Require at least one of the extras to be true
			if (!(flags || onboarding)) return false

			if (!force) {
				const state = api.getState()

				if (flags && flags$(state)) return false
				if (onboarding && onboarding$(state)) return false
			}
		},
	},
)

const parseSystemInfoFetchArgs = ({
	flags,
	onboarding,
	...extra
}: SystemInfoArg): SetRequired<SystemInfoArg, 'flags' | 'onboarding'> => ({
	flags: flags ?? SYSTEM_INFO_DEFAULT_ARGS.flags,
	onboarding: onboarding ?? SYSTEM_INFO_DEFAULT_ARGS.onboarding,
	...extra,
})

const SYSTEM_INFO_DEFAULT_ARGS: SystemInfoFetchArgs = {
	flags: true,
	onboarding: false,
}
